import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import PropertyCard from '@/components/ui/PropertyCard';

import { getPropertiesByLocation } from '@/services/property';
import { localities } from '@/services/localityData';
import { PropertyType } from '@/types/property';
import SEO from '@/components/seo/seo';
import { toSentenceCase } from '@/helper';

const LocationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [properties, setProperties] = useState<PropertyType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Find static locality data
  const localityInfo = localities.find(l =>
    l.slug === id ||
    l.name.toLowerCase() === id?.toLowerCase() ||
    l.name.toLowerCase().replace(/\s+/g, '-') === id?.toLowerCase()
  );

  useEffect(() => {
    const fetchProperties = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const searchName = id;
        const data = await getPropertiesByLocation(searchName);
        setProperties(data.properties);
      } catch (err: any) {
        console.error("Error fetching location properties:", err);
        // If 404, it just means no properties found, which is fine
        if (err.status === 404) {
          setProperties([]);
        } else {
          setError("Failed to load properties.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperties();
  }, [id]);

  if (isLoading) {
    return (
      <main className="flex-grow pt-32 pb-16 flex flex-col">
        <div className="container mx-auto px-4">
          {/* Back Button Skeleton */}
          {/* <div className="mb-8">
              <Skeleton className="h-6 w-32" />
            </div> */}

          {/* Location Header Skeleton */}
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden mb-12">
            <Skeleton className="w-full h-64 md:h-80" />
          </div>

          {/* Properties Grid Skeleton */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-32" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-3xl overflow-hidden border bg-card">
                  <Skeleton className="w-full h-[300px]" />
                  <div className="p-6 md:p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Skeleton className="h-5 w-5 rounded-full" />
                      <Skeleton className="h-4 w-40" />
                    </div>
                    <Skeleton className="h-7 w-full mb-2" />
                    <Skeleton className="h-7 w-3/4 mb-5" />
                    <div className="flex items-baseline gap-2 mb-5">
                      <Skeleton className="h-9 w-40" />
                    </div>
                    <div className="flex justify-between border-t pt-5 gap-4">
                      <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-8" /><Skeleton className="h-3 w-10" /></div></div>
                      <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-8" /><Skeleton className="h-3 w-10" /></div></div>
                      <div className="flex gap-2"><Skeleton className="h-10 w-10" /><div className="space-y-1"><Skeleton className="h-4 w-12" /><Skeleton className="h-3 w-10" /></div></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <SEO
        title={`${toSentenceCase(localityInfo?.name)} | Urban Oasis UAE`}
        description="Discover the most desirable neighborhoods and communities across Dubai, from waterfront living to urban hubs and family-friendly communities."
        url={`/locations/${id}`}
        image="/image/logo.png"
        keywords="dubai real estate, urban oasis"
      />
      <main className="flex-grow pt-32 pb-16 flex flex-col">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <Link to="/locations" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Locations
          </Link>

          {/* Location Header */}
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden mb-12">
            <div className="relative h-64 md:h-80">
              <img
                src={localityInfo?.projectImage || '/placeholder.svg'}
                alt={localityInfo?.name || id}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6 md:p-8">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                  {localityInfo?.name || id}
                </h1>
                <div className="flex items-center text-white/90">
                  <MapPin className="h-5 w-5 mr-2 text-gold" />
                  <span className="text-lg">Premium Location in Dubai</span>
                </div>
              </div>
            </div>
            {localityInfo?.about && (
              <div className="p-6 md:p-8 bg-card">
                <h3 className="text-xl font-semibold mb-3">About {localityInfo.name}</h3>
                <p className="text-muted-foreground leading-relaxed max-w-4xl">
                  {localityInfo.about}
                </p>
              </div>
            )}
          </div>

          {/* Properties Grid */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Properties in {localityInfo?.name || id}</h2>
              <span className="text-muted-foreground">{properties.length} Properties Found</span>
            </div>

            {properties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {properties.map((property) => (
                  <PropertyCard key={property._id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-secondary/20 rounded-xl border border-dashed">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Properties Found</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  We couldn't find any active listings in this location at the moment.
                  Please check back later or explore other locations.
                </p>
                <Button className="mt-6" asChild>
                  <Link to="/properties">View All Properties</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default LocationDetail;
